import React, { useMemo } from "react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";
import {
  DemographicsFields,
  DemographicsValue,
  emptyDemographics,
} from "./DemographicsFields";

interface MemberProfilesProps {
  defaultValues?: MemberProfilesForm;
  onSubmit: (data: MemberProfilesForm) => void | Promise<void>;
  isSaving?: boolean;
  // The head-of-household's own (login) email. It is not entered in this form, but
  // adult members must not reuse it — a shared email collapses two applicants onto
  // one ResMan prospect/PersonID and the lease is rejected. Threaded into the yup
  // resolver via react-hook-form `context` for the member email uniqueness test.
  primaryApplicantEmail?: string;
}

const ADULT_RELATIONSHIPS = [
  "Spouse/Cohabitant",
  "Family Member - Adult",
  "Roommate",
  "Live-In Aide",
] as const;

const isAdultRelationship = (rel: string) =>
  (ADULT_RELATIONSHIPS as readonly string[]).includes(rel);

const GOV_ID_TYPE_KEYS = [
  "driversLicense",
  "stateId",
  "passport",
  "militaryId",
  "itin",
  "foreignGovId",
  "other",
] as const;
type GovIdType = (typeof GOV_ID_TYPE_KEYS)[number] | "";
type StudentStatus = "FT" | "PT" | "NA" | "";

type AnticipatedReason =
  | "newborn"
  | "adoption"
  | "marriage"
  | "roommate"
  | "other"
  | "";

type YesNo = "yes" | "no" | "";

interface HouseholdMember {
  firstName: string;
  middleName: string;
  lastName: string;
  relationship: string;
  // Anticipated household additions within the next 12 months (newborns,
  // adoptions, future spouse/roommate). When isAnticipated=true, identity
  // and contact fields are not required because they typically don't exist
  // yet (e.g. SSN for an unborn child) — property staff collect at move-in.
  isAnticipated: boolean;
  anticipatedReason: AnticipatedReason;
  anticipatedArrival: string; // MM/YYYY
  dateOfBirth: string;
  ssn: string;
  noSsn: boolean;
  email: string;
  phone: string;
  govIdType: GovIdType;
  govId: string;
  studentStatus: StudentStatus;
  demographics: DemographicsValue;
  // Custody / guardianship — required for minors under HUD/PBRA/811. The
  // threshold is physical custody ≥50% of the time (HUD's household-count
  // standard). When the applicant lacks majority custody, we capture the
  // arrangement and (if a court order exists) require an upload — slot
  // created in documentRequirements.
  hasMajorityCustody: YesNo;
  custodyPercent: string;
  hasCourtOrder: YesNo;
  // Is this minor a foster placement? Foster children map to the ResMan
  // "Foster Child" household status (and get LIHTC foster treatment). Per-member
  // because the household-level fosterCareHousehold flag can't identify which
  // member is the foster child.
  isFosterPlacement: YesNo;
}

interface MemberProfilesForm {
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  dateOfBirth: string;
  ssn: string;
  noSsn: boolean;
  govIdType: GovIdType;
  govId: string;
  studentStatus: StudentStatus;
  demographics: DemographicsValue;
  // Household-composition question for TAA Supplemental §3 (`comp_foster`):
  // will any foster children or foster adults live in the household? Foster
  // persons get special income/household-size treatment under LIHTC rules.
  // Asked here alongside the household roster (moved from Student Details).
  fosterCareHousehold: YesNo;
  // Explicit answer to "will anyone else live here". Empty until they choose.
  willAnyoneElseLive: YesNo;
  members: HouseholdMember[];
}

const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
const dobRegex = /^\d{2}\/\d{2}\/\d{4}$/;

const isValidCalendarDob = (dob: string): boolean => {
  if (!dobRegex.test(dob)) return false;
  const [mm, dd, yyyy] = dob.split("/").map(Number);
  if (mm < 1 || mm > 12 || dd < 1 || yyyy < 1900) return false;
  const birth = new Date(yyyy, mm - 1, dd);
  return (
    !isNaN(birth.getTime()) &&
    birth.getFullYear() === yyyy &&
    birth.getMonth() === mm - 1 &&
    birth.getDate() === dd
  );
};

const isAdultDob = (dob: string): boolean => {
  if (!isValidCalendarDob(dob)) return false;
  const [mm, dd, yyyy] = dob.split("/").map(Number);
  const birth = new Date(yyyy, mm - 1, dd);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 18;
};

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits.length ? `(${digits}` : "";
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

function dobToDateInput(value: string | undefined): string {
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value ?? "");
  if (!us || !isValidCalendarDob(value ?? "")) return "";
  return `${us[3]}-${us[1]}-${us[2]}`;
}

function dateInputToDob(value: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!iso) return "";
  const asUs = `${iso[2]}/${iso[3]}/${iso[1]}`;
  return isValidCalendarDob(asUs) ? asUs : "";
}

function dobInputMax(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

const formatSsn = (value: string): string => {
  return value.replace(/\D/g, "").slice(0, 9);
};

export const MemberProfiles: React.FC<MemberProfilesProps> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
  primaryApplicantEmail,
}) => {
  const { t } = useTranslation();
  // Relationship options with i18n labels but stable string values
  const relationshipOptions = [
    { value: "Spouse/Cohabitant", label: t("account.leaseApplication.spouse") },
    {
      value: "Family Member - Adult",
      label: t("account.leaseApplication.familyAdult"),
    },
    { value: "Roommate", label: t("account.leaseApplication.roommate") },
    {
      value: "Live-In Aide",
      label: t("affordable.application.memberProfiles.liveInAide"),
    },
    {
      value: "Family Member - Minor",
      label: t("account.leaseApplication.familyMinor"),
    },
  ];

  const demographicsRequired = t(
    "affordable.application.demographics.required"
  ) as string;

  const demographicsSchema = yup.object({
    declined: yup.boolean().default(false),
    sex: yup.string().when("declined", {
      is: false,
      then: (s) =>
        s
          .oneOf(["M", "F", "X"], demographicsRequired)
          .required(demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
    ethnicity: yup.string().when("declined", {
      is: false,
      then: (s) =>
        s
          .oneOf(["1", "2", "3"], demographicsRequired)
          .required(demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
    race: yup.array().when("declined", {
      is: false,
      then: (s) => s.min(1, demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
    disabled: yup.string().when("declined", {
      is: false,
      then: (s) =>
        s
          .oneOf(["1", "2", "3"], demographicsRequired)
          .required(demographicsRequired),
      otherwise: (s) => s.notRequired(),
    }),
  });

  // Schema rules for anticipated members: name + relationship + reason +
  // arrival month are required; identity/contact fields are softened to
  // "optional when known" because newborns/future roommates typically lack
  // SSN/DOB/email until move-in.
  const memberSchema = yup.object({
    firstName: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.firstNameRequired") as string
      ),
    middleName: yup.string().default(""),
    lastName: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.lastNameRequired") as string
      ),
    relationship: yup
      .string()
      .required(
        t(
          "affordable.application.memberProfiles.relationshipRequired"
        ) as string
      ),
    isAnticipated: yup.boolean().default(false),
    anticipatedReason: yup.string().when("isAnticipated", {
      is: true,
      then: (s) =>
        s
          .oneOf(
            ["newborn", "adoption", "marriage", "roommate", "other"],
            t(
              "affordable.application.memberProfiles.anticipatedReasonRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.memberProfiles.anticipatedReasonRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    anticipatedArrival: yup.string().when("isAnticipated", {
      is: true,
      then: (s) =>
        s
          .required(
            t(
              "affordable.application.memberProfiles.anticipatedArrivalRequired"
            ) as string
          )
          .matches(
            /^(0[1-9]|1[0-2])\/\d{4}$/,
            t(
              "affordable.application.memberProfiles.anticipatedArrivalError"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    dateOfBirth: yup
      .string()
      .when(["relationship", "isAnticipated"], {
        is: (rel: string, anticipated: boolean) =>
          rel === "Family Member - Minor" && !anticipated,
        then: (s) =>
          s.required(
            t("affordable.application.memberProfiles.dobRequired") as string
          ),
        otherwise: (s) => s.notRequired(),
      })
      .test(
        "real-date",
        t("affordable.application.memberProfiles.dobError") as string,
        (val) => !val || isValidCalendarDob(val)
      ),
    ssn: yup.string().when(["relationship", "noSsn", "isAnticipated"], {
      is: (rel: string, noSsn: boolean, anticipated: boolean) =>
        rel === "Family Member - Minor" && !noSsn && !anticipated,
      then: (s) =>
        s
          .required(
            t("affordable.application.memberProfiles.ssnRequired") as string
          )
          .matches(
            /^\d{9}$/,
            t("affordable.application.memberProfiles.ssnDigits") as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    noSsn: yup.boolean().default(false),
    email: yup.string().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        isAdultRelationship(rel) && !anticipated,
      then: (s) =>
        s
          .email(t("account.registerProspect.emailInvalid") as string)
          .required(
            t("affordable.application.memberProfiles.emailRequired") as string
          )
          // Each adult applicant becomes their own ResMan prospect, keyed on email.
          // If two adults share an email the dedup collapses them onto one PersonID
          // and ResMan rejects the whole lease (PersonLease duplicate key). Catch it
          // here, inline, as the member is entered — not at submit.
          .test(
            "unique-applicant-email",
            t("affordable.application.memberProfiles.emailDuplicate") as string,
            function (value) {
              if (!value) return true;
              const email = value.trim().toLowerCase();
              const ctx = this.options.context as
                | { primaryEmail?: string }
                | undefined;
              const primaryEmail = (ctx?.primaryEmail || "")
                .trim()
                .toLowerCase();
              if (primaryEmail && email === primaryEmail) {
                return this.createError({
                  message: t(
                    "affordable.application.memberProfiles.emailMatchesPrimary"
                  ) as string,
                });
              }
              // `from` walks the ancestor chain; the outermost value is the whole
              // form, whose `members` array holds every entered member.
              const from = (
                this as unknown as {
                  from?: Array<{
                    value: { members?: Array<{ email?: string }> };
                  }>;
                }
              ).from;
              const root = from?.[from.length - 1]?.value;
              const members = Array.isArray(root?.members)
                ? root!.members!
                : [];
              const sameEmailCount = members.filter(
                (m) => (m?.email || "").trim().toLowerCase() === email
              ).length;
              if (sameEmailCount > 1) {
                return this.createError({
                  message: t(
                    "affordable.application.memberProfiles.emailDuplicate"
                  ) as string,
                });
              }
              return true;
            }
          ),
      otherwise: (s) => s.notRequired(),
    }),
    phone: yup.string().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        isAdultRelationship(rel) && !anticipated,
      then: (s) =>
        s
          .required(t("account.registerProspect.required") as string)
          .matches(
            phoneRegex,
            t("affordable.application.memberProfiles.phoneError") as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    // Gov't ID is required for adult household members — compliance needs a
    // government-issued ID for every adult in the unit. Minors and anticipated
    // members stay exempt (they typically have no ID), but a value is still
    // collectable (e.g. a minor with a passport).
    govIdType: yup.string().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        isAdultRelationship(rel) && !anticipated,
      then: (s) =>
        s.required(
          t("affordable.application.memberProfiles.govIdTypeRequired") as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    govId: yup.string().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        isAdultRelationship(rel) && !anticipated,
      then: (s) =>
        s.required(
          t("affordable.application.memberProfiles.govIdRequired") as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    studentStatus: yup.string().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        !isAdultRelationship(rel) && !anticipated,
      then: (s) =>
        s
          .oneOf(["FT", "PT", "NA"])
          .required(
            t(
              "affordable.application.memberProfiles.studentStatusRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    demographics: yup.object().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        !isAdultRelationship(rel) && !anticipated,
      then: () => demographicsSchema,
      otherwise: (s) => s.notRequired(),
    }),
    // Custody / legal guardianship for minors. Skipped for anticipated
    // minors (court orders typically don't exist for newborns/adoptees yet).
    hasMajorityCustody: yup.string().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        rel === "Family Member - Minor" && !anticipated,
      then: (s) =>
        s
          .oneOf(
            ["yes", "no"],
            t(
              "affordable.application.memberProfiles.hasMajorityCustodyRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.memberProfiles.hasMajorityCustodyRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    custodyPercent: yup
      .string()
      .when(["relationship", "isAnticipated", "hasMajorityCustody"], {
        is: (rel: string, anticipated: boolean, full: string) =>
          rel === "Family Member - Minor" && !anticipated && full === "no",
        then: (s) =>
          s.required(
            t(
              "affordable.application.memberProfiles.custodyPercentRequired"
            ) as string
          ),
        otherwise: (s) => s.notRequired(),
      }),
    hasCourtOrder: yup
      .string()
      .when(["relationship", "isAnticipated", "hasMajorityCustody"], {
        is: (rel: string, anticipated: boolean, full: string) =>
          rel === "Family Member - Minor" && !anticipated && full === "no",
        then: (s) =>
          s
            .oneOf(
              ["yes", "no"],
              t(
                "affordable.application.memberProfiles.hasCourtOrderRequired"
              ) as string
            )
            .required(
              t(
                "affordable.application.memberProfiles.hasCourtOrderRequired"
              ) as string
            ),
        otherwise: (s) => s.notRequired(),
      }),
    // Foster placement (minors only). Drives the ResMan "Foster Child" status.
    isFosterPlacement: yup.string().when(["relationship", "isAnticipated"], {
      is: (rel: string, anticipated: boolean) =>
        rel === "Family Member - Minor" && !anticipated,
      then: (s) =>
        s
          .oneOf(
            ["yes", "no"],
            t(
              "affordable.application.memberProfiles.isFosterPlacementRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.memberProfiles.isFosterPlacementRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const schema = yup.object({
    firstName: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.firstNameRequired") as string
      ),
    middleName: yup.string().default(""),
    lastName: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.lastNameRequired") as string
      ),
    phone: yup
      .string()
      .required(t("account.registerProspect.required") as string)
      .matches(
        phoneRegex,
        t("affordable.application.memberProfiles.phoneError") as string
      ),
    dateOfBirth: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.dobRequired") as string
      )
      .matches(
        dobRegex,
        t("affordable.application.memberProfiles.dobError") as string
      )
      .test(
        "real-date",
        t("affordable.application.memberProfiles.dobError") as string,
        (val) => !val || isValidCalendarDob(val)
      )
      .test(
        "is-adult",
        t("affordable.application.memberProfiles.mustBe18") as string,
        (val) => !val || isAdultDob(val)
      ),
    ssn: yup.string().when("noSsn", {
      is: false,
      then: (s) =>
        s
          .required(
            t("affordable.application.memberProfiles.ssnRequired") as string
          )
          .matches(
            /^\d{9}$/,
            t("affordable.application.memberProfiles.ssnDigits") as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    noSsn: yup.boolean().default(false),
    govIdType: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.govIdTypeRequired") as string
      ),
    govId: yup
      .string()
      .required(
        t("affordable.application.memberProfiles.govIdRequired") as string
      ),
    studentStatus: yup
      .string()
      .oneOf(["FT", "PT", "NA"])
      .required(
        t(
          "affordable.application.memberProfiles.studentStatusRequired"
        ) as string
      ),
    demographics: demographicsSchema,
    fosterCareHousehold: yup
      .string()
      .oneOf(
        ["yes", "no"],
        t("affordable.application.studentDetails.answerRequired") as string
      )
      .required(
        t("affordable.application.studentDetails.answerRequired") as string
      ),
    willAnyoneElseLive: yup
      .string()
      .oneOf(
        ["yes", "no"],
        t("affordable.application.memberProfiles.answerRequired") as string
      )
      .required(
        t("affordable.application.memberProfiles.answerRequired") as string
      ),
    members: yup
      .array()
      .of(memberSchema)
      .test(
        "occupants-answered",
        t("affordable.application.memberProfiles.memberRequired") as string,
        function (members) {
          if (this.parent?.willAnyoneElseLive !== "yes") return true;
          return Array.isArray(members) && members.length > 0;
        }
      ),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MemberProfilesForm>({
    resolver: yupResolver(schema) as any,
    // Expose the head-of-household email to the yup member-email uniqueness test.
    context: { primaryEmail: primaryApplicantEmail },
    defaultValues: {
      ...(defaultValues || {
        firstName: "",
        middleName: "",
        lastName: "",
        phone: "",
        dateOfBirth: "",
        ssn: "",
        noSsn: false,
        govIdType: "",
        studentStatus: "" as StudentStatus,
        govId: "",
        demographics: emptyDemographics,
        fosterCareHousehold: "" as YesNo,
        members: [],
      }),
      members: defaultValues?.members ?? [],
      willAnyoneElseLive:
        defaultValues?.willAnyoneElseLive === "yes" ||
        defaultValues?.willAnyoneElseLive === "no"
          ? defaultValues.willAnyoneElseLive
          : defaultValues?.members?.length
          ? "yes"
          : "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "members",
  });

  const noSsn = watch("noSsn");
  const willAnyoneElseLive = watch("willAnyoneElseLive");

  const prequalifiedHouseholdSize = useMemo(() => {
    const raw = sessionStorage.getItem("tp_prequalify_household_size");
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  }, []);

  const totalMembersListed = 1 + fields.length;
  const showHouseholdSizeMismatch =
    prequalifiedHouseholdSize !== null &&
    prequalifiedHouseholdSize > 1 &&
    totalMembersListed !== prequalifiedHouseholdSize;

  const handleAddMember = () => {
    append({
      firstName: "",
      middleName: "",
      lastName: "",
      relationship: "",
      isAnticipated: false,
      anticipatedReason: "" as AnticipatedReason,
      anticipatedArrival: "",
      dateOfBirth: "",
      ssn: "",
      noSsn: false,
      email: "",
      phone: "",
      govIdType: "",
      govId: "",
      studentStatus: "" as StudentStatus,
      demographics: emptyDemographics,
      hasMajorityCustody: "" as YesNo,
      custodyPercent: "",
      hasCourtOrder: "" as YesNo,
      isFosterPlacement: "" as YesNo,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Section 1: Primary Applicant Info */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("account.leaseApplication.applicantInfo")}
        </RegisterReviewSectionTitle>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t("formInput.firstName")} *`}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="middleName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t("formInput.middleName")}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t("formInput.lastName")} *`}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t("account.leaseApplication.phone")} *`}
                  placeholder="(555) 555-5555"
                  inputProps={{ inputMode: "tel" }}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  onChange={(e) => field.onChange(formatPhone(e.target.value))}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="dateOfBirth"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  fullWidth
                  value={dobToDateInput(field.value)}
                  onChange={(e) =>
                    field.onChange(dateInputToDob(e.target.value))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: "1900-01-01", max: dobInputMax() }}
                  label={`${t("account.leaseApplication.birthdate")} *`}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="ssn"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.memberProfiles.ssnLabel"
                  )} *`}
                  placeholder="123456789"
                  type="password"
                  inputProps={{ inputMode: "numeric", maxLength: 9 }}
                  disabled={noSsn}
                  error={!!errors.ssn}
                  helperText={errors.ssn?.message}
                  onChange={(e) => field.onChange(formatSsn(e.target.value))}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="noSsn"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t("account.leaseApplication.noSsn")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              color="primary"
              sx={{ fontSize: 14, fontWeight: 600, mt: 1, mb: 0.5 }}
            >
              {t("affordable.application.memberProfiles.idSectionLabel")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Controller
              name="govIdType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.govIdType}>
                  <InputLabel>
                    {`${t(
                      "affordable.application.memberProfiles.govIdTypeLabel"
                    )} *`}
                  </InputLabel>
                  <Select
                    {...field}
                    label={`${t(
                      "affordable.application.memberProfiles.govIdTypeLabel"
                    )} *`}
                  >
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    {GOV_ID_TYPE_KEYS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(
                          `affordable.application.memberProfiles.govIdTypes.${key}`
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.govIdType && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.5 }}
                    >
                      {errors.govIdType.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <Controller
              name="govId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.memberProfiles.govIdLabel"
                  )} *`}
                  inputProps={{ autoComplete: "off" }}
                  error={!!errors.govId}
                  helperText={errors.govId?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              color="primary"
              sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
            >
              {`${t(
                "affordable.application.memberProfiles.studentStatusLabel"
              )} *`}
            </Typography>
            <Controller
              name="studentStatus"
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.studentStatus}>
                  <RadioGroup row {...field} sx={{ gap: 2, flexWrap: "wrap" }}>
                    <FormControlLabel
                      value="FT"
                      control={<Radio size="small" />}
                      label={t(
                        "affordable.application.memberProfiles.studentStatusFullTime"
                      )}
                      sx={{ mr: 0 }}
                    />
                    <FormControlLabel
                      value="PT"
                      control={<Radio size="small" />}
                      label={t(
                        "affordable.application.memberProfiles.studentStatusPartTime"
                      )}
                      sx={{ mr: 0 }}
                    />
                    <FormControlLabel
                      value="NA"
                      control={<Radio size="small" />}
                      label={t(
                        "affordable.application.memberProfiles.studentStatusNotStudent"
                      )}
                      sx={{ mr: 0 }}
                    />
                  </RadioGroup>
                  {errors.studentStatus && (
                    <FormHelperText>
                      {errors.studentStatus.message as string}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <DemographicsFields control={control} basePath="demographics" />
          </Grid>
        </Grid>
      </RegisterReviewSection>

      {/* Foster household composition (TAA Supplemental §3, moved from Student Details) */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle sx={{ mb: 1 }}>
          {t("affordable.application.studentDetails.fosterQuestion")}
        </RegisterReviewSectionTitle>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          {t("affordable.application.studentDetails.fosterHelp")}
        </Typography>
        <Controller
          name="fosterCareHousehold"
          control={control}
          render={({ field }) => (
            <FormControl error={!!errors.fosterCareHousehold}>
              <RadioGroup row {...field}>
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" />}
                  label={t("affordable.application.studentDetails.yes")}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label={t("affordable.application.studentDetails.no")}
                  sx={{ ml: 2 }}
                />
              </RadioGroup>
              {errors.fosterCareHousehold && (
                <FormHelperText>
                  {errors.fosterCareHousehold.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      </RegisterReviewSection>

      {/* Section 2: Household Members */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle sx={{ mb: 2 }}>
          {t("affordable.application.memberProfiles.willAnyoneLive")}
        </RegisterReviewSectionTitle>
        <Controller
          name="willAnyoneElseLive"
          control={control}
          render={({ field }) => (
            <FormControl error={!!errors.willAnyoneElseLive} sx={{ mb: 2 }}>
              <RadioGroup
                row
                value={field.value}
                onBlur={field.onBlur}
                onChange={(e) => {
                  const value = e.target.value as YesNo;
                  field.onChange(value);
                  if (value === "no") remove();
                  if (value === "yes" && fields.length === 0) handleAddMember();
                }}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" />}
                  label={t("affordable.application.memberProfiles.yes")}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label={t("affordable.application.memberProfiles.no")}
                  sx={{ ml: 2 }}
                />
              </RadioGroup>
              {errors.willAnyoneElseLive && (
                <FormHelperText>
                  {errors.willAnyoneElseLive.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        {willAnyoneElseLive === "yes" && showHouseholdSizeMismatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("affordable.application.memberProfiles.householdSizeMismatch", {
              expected: prequalifiedHouseholdSize,
              actual: totalMembersListed,
            })}
          </Alert>
        )}

        {willAnyoneElseLive === "yes" && fields.length === 0 && (
          <Box
            sx={{
              border: `2px dashed ${COLORS.gray[300]}`,
              borderRadius: 2,
              p: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {t("affordable.application.memberProfiles.emptyState")}
            </Typography>
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={handleAddMember}
              sx={{ mt: 1 }}
            >
              {t("affordable.application.memberProfiles.addFirstMember")}
            </Button>
          </Box>
        )}

        {willAnyoneElseLive === "yes" &&
          fields.map((field, index) => {
            const memberErrors = errors.members?.[index];
            const relationship = watch(`members.${index}.relationship`);
            const memberNoSsn = watch(`members.${index}.noSsn`);
            const isAnticipated = watch(`members.${index}.isAnticipated`);
            const hasMajorityCustody = watch(
              `members.${index}.hasMajorityCustody`
            );
            const isAdult = isAdultRelationship(relationship);

            return (
              <Box
                key={field.id}
                sx={{
                  border: `1px solid ${COLORS.gray[200]}`,
                  borderRadius: 2,
                  p: 3,
                  mb: 2,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h3Bold" color="primary">
                    {t("affordable.application.memberProfiles.memberLabel")} #
                    {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => remove(index)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name={`members.${index}.relationship`}
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!memberErrors?.relationship}
                        >
                          <InputLabel>
                            {`${t("account.leaseApplication.relationship")} *`}
                          </InputLabel>
                          <Select
                            {...field}
                            label={`${t(
                              "account.leaseApplication.relationship"
                            )} *`}
                          >
                            {relationshipOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {memberErrors?.relationship && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, ml: 1.5 }}
                            >
                              {memberErrors.relationship.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {relationship && (
                    <>
                      {/* Current vs anticipated household member */}
                      <Grid item xs={12}>
                        <Controller
                          name={`members.${index}.isAnticipated`}
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth>
                              <InputLabel>
                                {t(
                                  "affordable.application.memberProfiles.livingNowOrAnticipated"
                                )}
                              </InputLabel>
                              <Select
                                {...field}
                                value={field.value ? "anticipated" : "current"}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value === "anticipated"
                                  )
                                }
                                label={t(
                                  "affordable.application.memberProfiles.livingNowOrAnticipated"
                                )}
                              >
                                <MenuItem value="current">
                                  {t(
                                    "affordable.application.memberProfiles.livingNow"
                                  )}
                                </MenuItem>
                                <MenuItem value="anticipated">
                                  {t(
                                    "affordable.application.memberProfiles.anticipated"
                                  )}
                                </MenuItem>
                              </Select>
                              <Typography
                                variant="caption2"
                                color="text.secondary"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                {t(
                                  "affordable.application.memberProfiles.anticipatedHelper"
                                )}
                              </Typography>
                            </FormControl>
                          )}
                        />
                      </Grid>

                      {isAnticipated && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name={`members.${index}.anticipatedReason`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  fullWidth
                                  error={!!memberErrors?.anticipatedReason}
                                >
                                  <InputLabel>{`${t(
                                    "affordable.application.memberProfiles.anticipatedReasonLabel"
                                  )} *`}</InputLabel>
                                  <Select
                                    {...field}
                                    label={`${t(
                                      "affordable.application.memberProfiles.anticipatedReasonLabel"
                                    )} *`}
                                  >
                                    <MenuItem value="newborn">
                                      {t(
                                        "affordable.application.memberProfiles.anticipatedReasons.newborn"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="adoption">
                                      {t(
                                        "affordable.application.memberProfiles.anticipatedReasons.adoption"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="marriage">
                                      {t(
                                        "affordable.application.memberProfiles.anticipatedReasons.marriage"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="roommate">
                                      {t(
                                        "affordable.application.memberProfiles.anticipatedReasons.roommate"
                                      )}
                                    </MenuItem>
                                    <MenuItem value="other">
                                      {t(
                                        "affordable.application.memberProfiles.anticipatedReasons.other"
                                      )}
                                    </MenuItem>
                                  </Select>
                                  {memberErrors?.anticipatedReason && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ mt: 0.5, ml: 1.5 }}
                                    >
                                      {memberErrors.anticipatedReason.message}
                                    </Typography>
                                  )}
                                </FormControl>
                              )}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Controller
                              name={`members.${index}.anticipatedArrival`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={`${t(
                                    "affordable.application.memberProfiles.anticipatedArrivalLabel"
                                  )} *`}
                                  placeholder="MM/YYYY"
                                  inputProps={{ inputMode: "numeric" }}
                                  error={!!memberErrors?.anticipatedArrival}
                                  helperText={
                                    memberErrors?.anticipatedArrival?.message ??
                                    t(
                                      "affordable.application.memberProfiles.anticipatedArrivalHelper"
                                    )
                                  }
                                />
                              )}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} sm={4}>
                        <Controller
                          name={`members.${index}.firstName`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label={`${t("formInput.firstName")} *`}
                              error={!!memberErrors?.firstName}
                              helperText={memberErrors?.firstName?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Controller
                          name={`members.${index}.middleName`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label={t("formInput.middleName")}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Controller
                          name={`members.${index}.lastName`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label={`${t("formInput.lastName")} *`}
                              error={!!memberErrors?.lastName}
                              helperText={memberErrors?.lastName?.message}
                            />
                          )}
                        />
                      </Grid>

                      {relationship !== "Live-In Aide" && (
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`members.${index}.dateOfBirth`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                type="date"
                                fullWidth
                                value={dobToDateInput(field.value)}
                                onChange={(e) =>
                                  field.onChange(dateInputToDob(e.target.value))
                                }
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                  min: "1900-01-01",
                                  max: dobInputMax(),
                                }}
                                label={`${t(
                                  "account.leaseApplication.birthDate"
                                )}${!isAdult && !isAnticipated ? " *" : ""}`}
                                error={!!memberErrors?.dateOfBirth}
                                helperText={memberErrors?.dateOfBirth?.message}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {!isAdult && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name={`members.${index}.ssn`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label={`${t(
                                    "affordable.application.memberProfiles.ssnLabel"
                                  )} *`}
                                  type="password"
                                  inputProps={{
                                    inputMode: "numeric",
                                    maxLength: 9,
                                  }}
                                  disabled={memberNoSsn}
                                  error={!!memberErrors?.ssn}
                                  helperText={memberErrors?.ssn?.message}
                                  onChange={(e) =>
                                    field.onChange(formatSsn(e.target.value))
                                  }
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Controller
                              name={`members.${index}.noSsn`}
                              control={control}
                              render={({ field }) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={field.value}
                                      onChange={(e) =>
                                        field.onChange(e.target.checked)
                                      }
                                    />
                                  }
                                  label={t(
                                    "affordable.application.memberProfiles.noSsnThirdPerson"
                                  )}
                                />
                              )}
                            />
                          </Grid>
                        </>
                      )}

                      {isAdult && (
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`members.${index}.email`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label={`${t("formInput.email")} *`}
                                type="email"
                                error={!!memberErrors?.email}
                                helperText={memberErrors?.email?.message}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {isAdult && (
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name={`members.${index}.phone`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label={`${t(
                                  "account.leaseApplication.phone"
                                )} *`}
                                error={!!memberErrors?.phone}
                                helperText={memberErrors?.phone?.message}
                                onChange={(e) =>
                                  field.onChange(formatPhone(e.target.value))
                                }
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {isAdult && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: "action.hover",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                color: "text.secondary",
                                lineHeight: 1.5,
                              }}
                            >
                              {t(
                                "affordable.application.memberProfiles.emailHelper"
                              )}
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Typography
                          color="primary"
                          sx={{ fontSize: 14, fontWeight: 600, mt: 1, mb: 0.5 }}
                        >
                          {`${t(
                            "affordable.application.memberProfiles.idSectionLabel"
                          )}${isAdult ? " *" : ""}`}
                        </Typography>
                        {!isAdult && (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", lineHeight: 1.5 }}
                          >
                            {t(
                              "affordable.application.memberProfiles.idSectionHelper"
                            )}
                          </Typography>
                        )}
                      </Grid>

                      {
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`members.${index}.govIdType`}
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                fullWidth
                                error={!!memberErrors?.govIdType}
                              >
                                <InputLabel>
                                  {t(
                                    "affordable.application.memberProfiles.govIdTypeLabel"
                                  )}
                                </InputLabel>
                                <Select
                                  {...field}
                                  label={t(
                                    "affordable.application.memberProfiles.govIdTypeLabel"
                                  )}
                                >
                                  <MenuItem value="">
                                    <em>—</em>
                                  </MenuItem>
                                  {GOV_ID_TYPE_KEYS.map((key) => (
                                    <MenuItem key={key} value={key}>
                                      {t(
                                        `affordable.application.memberProfiles.govIdTypes.${key}`
                                      )}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {memberErrors?.govIdType && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ mt: 0.5, ml: 1.5 }}
                                  >
                                    {memberErrors.govIdType.message}
                                  </Typography>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>
                      }

                      {
                        <Grid item xs={12} sm={8}>
                          <Controller
                            name={`members.${index}.govId`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label={t(
                                  "affordable.application.memberProfiles.govIdLabel"
                                )}
                                inputProps={{ autoComplete: "off" }}
                                error={!!memberErrors?.govId}
                                helperText={memberErrors?.govId?.message}
                              />
                            )}
                          />
                        </Grid>
                      }

                      {!isAdult && (
                        <Grid item xs={12}>
                          <Typography
                            color="primary"
                            sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
                          >
                            {`${t(
                              "affordable.application.memberProfiles.studentStatusLabel"
                            )} *`}
                          </Typography>
                          <Controller
                            name={`members.${index}.studentStatus`}
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                error={!!memberErrors?.studentStatus}
                              >
                                <RadioGroup
                                  row
                                  {...field}
                                  sx={{ gap: 2, flexWrap: "wrap" }}
                                >
                                  <FormControlLabel
                                    value="FT"
                                    control={<Radio size="small" />}
                                    label={t(
                                      "affordable.application.memberProfiles.studentStatusFullTime"
                                    )}
                                    sx={{ mr: 0 }}
                                  />
                                  <FormControlLabel
                                    value="PT"
                                    control={<Radio size="small" />}
                                    label={t(
                                      "affordable.application.memberProfiles.studentStatusPartTime"
                                    )}
                                    sx={{ mr: 0 }}
                                  />
                                  <FormControlLabel
                                    value="NA"
                                    control={<Radio size="small" />}
                                    label={t(
                                      "affordable.application.memberProfiles.studentStatusNotStudent"
                                    )}
                                    sx={{ mr: 0 }}
                                  />
                                </RadioGroup>
                                {memberErrors?.studentStatus && (
                                  <FormHelperText>
                                    {
                                      memberErrors.studentStatus
                                        .message as string
                                    }
                                  </FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>
                      )}

                      {!isAdult && (
                        <Grid item xs={12}>
                          <DemographicsFields
                            control={control}
                            basePath={`members.${index}.demographics`}
                            thirdPerson
                          />
                        </Grid>
                      )}

                      {!isAdult && !isAnticipated && (
                        <Grid item xs={12}>
                          <Typography
                            color="primary"
                            sx={{ fontSize: 14, fontWeight: 600, mt: 2, mb: 1 }}
                          >
                            {t(
                              "affordable.application.memberProfiles.custodySectionLabel"
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1.5 }}
                          >
                            {t(
                              "affordable.application.memberProfiles.custodySectionHelper"
                            )}
                          </Typography>

                          <Controller
                            name={`members.${index}.hasMajorityCustody`}
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                error={!!memberErrors?.hasMajorityCustody}
                                sx={{ mb: 1.5 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 0.5, fontWeight: 500 }}
                                >
                                  {`${t(
                                    "affordable.application.memberProfiles.hasMajorityCustodyLabel"
                                  )} *`}
                                </Typography>
                                <RadioGroup row {...field}>
                                  <FormControlLabel
                                    value="yes"
                                    control={<Radio size="small" />}
                                    label={t(
                                      "affordable.application.memberProfiles.yes"
                                    )}
                                  />
                                  <FormControlLabel
                                    value="no"
                                    control={<Radio size="small" />}
                                    label={t(
                                      "affordable.application.memberProfiles.no"
                                    )}
                                    sx={{ ml: 2 }}
                                  />
                                </RadioGroup>
                                {memberErrors?.hasMajorityCustody && (
                                  <FormHelperText>
                                    {
                                      memberErrors.hasMajorityCustody
                                        .message as string
                                    }
                                  </FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />

                          {hasMajorityCustody === "no" && (
                            <>
                              <Controller
                                name={`members.${index}.custodyPercent`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    fullWidth
                                    label={`${t(
                                      "affordable.application.memberProfiles.custodyPercentLabel"
                                    )} *`}
                                    placeholder={
                                      t(
                                        "affordable.application.memberProfiles.custodyPercentPlaceholder"
                                      ) as string
                                    }
                                    error={!!memberErrors?.custodyPercent}
                                    helperText={
                                      memberErrors?.custodyPercent?.message ??
                                      t(
                                        "affordable.application.memberProfiles.custodyPercentHelper"
                                      )
                                    }
                                    sx={{ mb: 1.5 }}
                                  />
                                )}
                              />

                              <Controller
                                name={`members.${index}.hasCourtOrder`}
                                control={control}
                                render={({ field }) => (
                                  <FormControl
                                    error={!!memberErrors?.hasCourtOrder}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ mb: 0.5, fontWeight: 500 }}
                                    >
                                      {`${t(
                                        "affordable.application.memberProfiles.hasCourtOrderLabel"
                                      )} *`}
                                    </Typography>
                                    <RadioGroup row {...field}>
                                      <FormControlLabel
                                        value="yes"
                                        control={<Radio size="small" />}
                                        label={t(
                                          "affordable.application.memberProfiles.yes"
                                        )}
                                      />
                                      <FormControlLabel
                                        value="no"
                                        control={<Radio size="small" />}
                                        label={t(
                                          "affordable.application.memberProfiles.no"
                                        )}
                                        sx={{ ml: 2 }}
                                      />
                                    </RadioGroup>
                                    {memberErrors?.hasCourtOrder && (
                                      <FormHelperText>
                                        {
                                          memberErrors.hasCourtOrder
                                            .message as string
                                        }
                                      </FormHelperText>
                                    )}
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mt: 0.5 }}
                                    >
                                      {t(
                                        "affordable.application.memberProfiles.hasCourtOrderHelper"
                                      )}
                                    </Typography>
                                  </FormControl>
                                )}
                              />
                            </>
                          )}

                          <Controller
                            name={`members.${index}.isFosterPlacement`}
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                error={!!memberErrors?.isFosterPlacement}
                                sx={{ mt: 1 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 0.5, fontWeight: 500 }}
                                >
                                  {`${t(
                                    "affordable.application.memberProfiles.isFosterPlacementLabel"
                                  )} *`}
                                </Typography>
                                <RadioGroup row {...field}>
                                  <FormControlLabel
                                    value="yes"
                                    control={<Radio size="small" />}
                                    label={t(
                                      "affordable.application.memberProfiles.yes"
                                    )}
                                  />
                                  <FormControlLabel
                                    value="no"
                                    control={<Radio size="small" />}
                                    label={t(
                                      "affordable.application.memberProfiles.no"
                                    )}
                                    sx={{ ml: 2 }}
                                  />
                                </RadioGroup>
                                {memberErrors?.isFosterPlacement && (
                                  <FormHelperText>
                                    {
                                      memberErrors.isFosterPlacement
                                        .message as string
                                    }
                                  </FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Box>
            );
          })}

        {willAnyoneElseLive === "yes" && fields.length > 0 && (
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleAddMember}
            sx={{ mt: 1 }}
          >
            {t("affordable.application.memberProfiles.addFirstMember")}
          </Button>
        )}
        {willAnyoneElseLive === "yes" &&
          (errors.members as { message?: string } | undefined)?.message && (
            <FormHelperText error>
              {(errors.members as { message?: string }).message}
            </FormHelperText>
          )}
      </RegisterReviewSection>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button variant="outlined" disabled>
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
